/**
 * 充电桩型号 PileFunctionEntity：mainPileFunction（外层主数据）+ subPileFunction（各嵌入式版本，结构一致）
 */

function formatDateTime(d) {
  const pad = (n) => (n < 10 ? '0' + n : '' + n);
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    ' ' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes()) +
    ':' +
    pad(d.getSeconds())
  );
}

export function stripEmbeddedCollections(raw) {
  const e = JSON.parse(JSON.stringify(raw || {}));
  delete e.otherEmbeddedVersionList;
  delete e.pileTypeVersionEntityList;
  return e;
}

/**
 * 将“基础信息/参数信息（截图中的那批字段）”聚合到 piletypeEntity
 * - 只做结构整理，不做业务变更
 * - 仅复制在 source 中存在的字段
 */
// 按接口约定：piletypeEntity 只承载这一组字段，其它基础信息留在 mainPileFunction
const PILETYPE_ENTITY_KEYS = [
  'pileTypeName',
  'pileTypeCode',
  'ratedCurrent',
  'ratedVoltage',
  'ratedPower',
  'gunNum',
  'chargingSpeed',
  'chargingSpeedName',
  'interfaceType',
  'interfaceTypeName',
  'currentOutType',
  'currentOutTypeName',
  'pileImgUrl',
  'useState',
  'useStateName',
  'isScreen',
  // 其他嵌入式版本编辑需要
  'pileTypeVersionId',
];

const PILETYPE_ENTITY_KEY_ALIASES = {
  // 兼容历史/后端字段大小写差异
  pileTypeCode: ['pileTypecode', 'PileTypecode', 'PileTypeCode'],
};

function getFirstDefinedValue(source, base, key) {
  if (source && Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
    return source[key];
  }
  if (base && Object.prototype.hasOwnProperty.call(base, key) && base[key] !== undefined) {
    return base[key];
  }
  const aliases = PILETYPE_ENTITY_KEY_ALIASES[key] || [];
  for (const a of aliases) {
    if (source && Object.prototype.hasOwnProperty.call(source, a) && source[a] !== undefined) return source[a];
    if (base && Object.prototype.hasOwnProperty.call(base, a) && base[a] !== undefined) return base[a];
  }
  return undefined;
}

function buildPiletypeEntity(source) {
  const s = source && typeof source === 'object' ? source : {};
  const base = (s.piletypeEntity && typeof s.piletypeEntity === 'object') ? s.piletypeEntity : {};
  const picked = {};
  PILETYPE_ENTITY_KEYS.forEach((k) => {
    const v = getFirstDefinedValue(s, base, k);
    // 为了保证“piletypeEntity字段齐全”，即便没有值也显式给 null（避免 undefined 在 JSON 中被省略）
    picked[k] = v === undefined ? null : v;
  });
  return { ...base, ...picked };
}

function removePiletypeEntityKeysFromRoot(obj) {
  if (!obj || typeof obj !== 'object') return;
  PILETYPE_ENTITY_KEYS.forEach((k) => {
    if (k in obj) delete obj[k];
  });
}

export function normalizePileFunctionNumericFields(entity) {
  if (!entity || typeof entity !== 'object') return;
  const numericKeys = [
    'hasReservation',
    'hasCurrentAdjustable',
    'hasFamilySharing',
    'hasChargingAlarm',
    'hasNationalStandardCompatibility',
    'activityType',
    'hasCardSwipingSupported',
    'hasPrivatePileSharing',
    'hasThreePinThermalControl',
    'adapterRecognition',
    'upgradeMethod',
    'countdownReservation',
    'currentSettingMemory',
    'adjustPower',
    'isScreen',
  ];
  numericKeys.forEach((k) => {
    if (!(k in entity)) return;
    const v = entity[k];
    if (v === '' || v === undefined) {
      entity[k] = null;
      return;
    }
    if (v === null) return;
    const n = Number(v);
    entity[k] = Number.isNaN(n) ? v : n;
  });
}

const FORM_SKIP_KEYS_FOR_MERGE = new Set(['pileTypeVersionEntityList', 'otherEmbeddedVersionList']);

export function mergeEmbeddedDeviceFormOntoEntity(entity, form) {
  if (!entity || !form) return;
  const prevAudioTypeName = entity.audioTypeName;
  const prevNetworkStatusName = entity.networkStatusName;
  Object.keys(form).forEach((k) => {
    if (FORM_SKIP_KEYS_FOR_MERGE.has(k)) return;
    const v = form[k];
    if (v === undefined) return;
    entity[k] = v;
  });
  entity.audioTypeName =
    form.audioTypeName !== undefined && form.audioTypeName !== ''
      ? form.audioTypeName
      : prevAudioTypeName;
  entity.networkStatusName =
    form.networkStatusName !== undefined && form.networkStatusName !== ''
      ? form.networkStatusName
      : prevNetworkStatusName;
  entity.softVersion =
    form.softVersion != null && form.softVersion !== '' ? form.softVersion : entity.softVersion || '';
}

/**
 * 计算 pileTypeVersionEntityList 表格行（与接口子表元数据一致）
 */
export function computePileTypeVersionList(p, form, mode, rowIndex, existingList, routePileTypeNo) {
  const list = JSON.parse(JSON.stringify(existingList || []));
  const existingRow =
    mode === 'edit' && rowIndex >= 0 && Array.isArray(list) && list[rowIndex]
      ? list[rowIndex]
      : {};
  const versionRow = {
    // 其他嵌入式版本行必须用该行 pileFunctionId，勿回退主表 p.functionId（多版本时主表 id 只是其中一行）
    pileFunctionId:
      mode === 'add' ? '' : form.pileFunctionId || existingRow.pileFunctionId || '',
    pileTypeNo: p.pileTypeNo || form.pileTypeNo || routePileTypeNo || '',
    softVersion: form.softVersion != null ? String(form.softVersion) : '',
    defaultFlag: Number(form.defaultFlag) === 1 ? 1 : 0,
    status: form.status === 1 || form.status === '1' ? 1 : 0,
    isDel: form.isDel || 'N',
  };
  // 编辑时必须继承原行 pileTypeVersionId（否则后续 updatePileVersion 会变成 null）
  if (form.pileTypeVersionId || existingRow.pileTypeVersionId) {
    versionRow.pileTypeVersionId = form.pileTypeVersionId || existingRow.pileTypeVersionId;
  }
  if (form.createdByName) versionRow.createdByName = form.createdByName;
  if (form.createdWhen) versionRow.createdWhen = form.createdWhen;
  if (form.lastModifiedByName) versionRow.lastModifiedByName = form.lastModifiedByName;
  if (form.lastModifiedWhen) versionRow.lastModifiedWhen = form.lastModifiedWhen;

  if (mode === 'add') {
    versionRow.createdWhen = versionRow.createdWhen || formatDateTime(new Date());
    list.push(versionRow);
  } else if (mode === 'edit' && rowIndex >= 0) {
    list[rowIndex] = { ...(list[rowIndex] || {}), ...versionRow };
  }

  if (Number(form.defaultFlag) === 1) {
    list.forEach((r, i) => {
      const isTarget = mode === 'add' ? i === list.length - 1 : i === rowIndex;
      r.defaultFlag = isTarget ? 1 : 0;
    });
  }
  return list;
}

/** 构造 onlyAdd 单实体与合并用 versionList（内部供 buildOnlyAddPileFunctionBody 使用） */
export function buildSavePileVersionPayload(parent, form, mode, rowIndex, existingList, routePileTypeNo) {
  const p = parent && typeof parent === 'object' ? parent : {};
  const versionList = computePileTypeVersionList(p, form, mode, rowIndex, existingList, routePileTypeNo);

  const mainPileFunction = stripEmbeddedCollections(p);
  // stripEmbeddedCollections 会把原对象里的 piletypeEntity 也拷贝进来，这里移除，确保 piletypeEntity 与 mainPileFunction 平级
  delete mainPileFunction.piletypeEntity;
  // 基础信息字段汇总到 piletypeEntity（与 mainPileFunction 平级）
  const piletypeEntity = buildPiletypeEntity(p);
  removePiletypeEntityKeysFromRoot(mainPileFunction);
  // 图片同时属于 t_pile_function（C端按版本取图），从 piletypeEntity 回填，避免被 removePiletypeEntityKeysFromRoot 删掉
  mainPileFunction.pileImgUrl = piletypeEntity.pileImgUrl;
  mainPileFunction.functionId = p.functionId || mainPileFunction.functionId || '';
  if (Number(form.defaultFlag) === 1) {
    mainPileFunction.defaultEmbeddedVersionNo =
      form.softVersion || mainPileFunction.defaultEmbeddedVersionNo;
  }
  delete mainPileFunction._clientId;
  delete mainPileFunction.embeddedVersionNo;
  delete mainPileFunction.isDefaultVersion;
  normalizePileFunctionNumericFields(mainPileFunction);

  // savePileVersion：只提交“本次新增/编辑的版本对象”，避免把其它版本的字段覆盖成 null
  const targetIndex = mode === 'add' ? versionList.length - 1 : rowIndex;
  const targetRow = versionList[targetIndex] || {};

  const subOne = stripEmbeddedCollections(p);
  delete subOne.piletypeEntity;
  removePiletypeEntityKeysFromRoot(subOne);
  // onlyAdd 的子版本实体同样回填图片（继承主版本图，C端按版本取图）
  subOne.pileImgUrl = piletypeEntity.pileImgUrl;
  // 子版本的设备功能字段来自当前表单
  mergeEmbeddedDeviceFormOntoEntity(subOne, form);
  // 子版本携带版本元数据；完整子表见返回体根级 pileTypeVersionEntityList
  if (targetRow && typeof targetRow === 'object') {
    Object.assign(subOne, {
      pileTypeVersionId: targetRow.pileTypeVersionId,
      pileFunctionId: targetRow.pileFunctionId,
      pileTypeNo: targetRow.pileTypeNo,
      defaultFlag: targetRow.defaultFlag,
      status: targetRow.status,
      isDel: targetRow.isDel,
      createdWhen: targetRow.createdWhen,
      createdByName: targetRow.createdByName,
      lastModifiedByName: targetRow.lastModifiedByName,
      lastModifiedWhen: targetRow.lastModifiedWhen,
    });
  }
  delete subOne._clientId;
  delete subOne.embeddedVersionNo;
  delete subOne.isDefaultVersion;
  normalizePileFunctionNumericFields(subOne);

  if (mode === 'edit') {
    // 子版本提交：仅本条 pileFunctionId（与 findByFunctionId 入参一致）；勿保留主表快照里的根 functionId
    const rowPileFn =
      (form && form.pileFunctionId) || (targetRow && targetRow.pileFunctionId) || '';
    if (rowPileFn) {
      const id = String(rowPileFn);
      subOne.pileFunctionId = id;
      subOne.functionId = id;
    }
  } else if (mode === 'add') {
    delete subOne.functionId;
    delete subOne.pileFunctionId;
  }

  return {
    mainPileFunction,
    piletypeEntity,
    subPileFunction: [subOne],
    versionList,
  };
}

/**
 * updatePileVersion：仅 mainPileFunction + piletypeEntity（子页编辑时把表单合并进 main）
 */
export function buildUpdatePileVersionPayload(parent, form, rowIndex, existingList, routePileTypeNo) {
  const p = parent && typeof parent === 'object' ? parent : {};
  const mode = 'edit';
  const versionList = computePileTypeVersionList(p, form, mode, rowIndex, existingList, routePileTypeNo);

  const mainPileFunction = stripEmbeddedCollections(p);
  delete mainPileFunction.piletypeEntity;
  const piletypeEntity = buildPiletypeEntity(p);
  piletypeEntity.pileTypeVersionId =
    (form && form.pileTypeVersionId) || (versionList[rowIndex] && versionList[rowIndex].pileTypeVersionId) || null;
  removePiletypeEntityKeysFromRoot(mainPileFunction);
  // 图片同时属于 t_pile_function（C端按版本取图），从 piletypeEntity 回填，避免被 removePiletypeEntityKeysFromRoot 删掉
  mainPileFunction.pileImgUrl = piletypeEntity.pileImgUrl;
  mainPileFunction.functionId = p.functionId || mainPileFunction.functionId || '';
  delete mainPileFunction._clientId;
  delete mainPileFunction.embeddedVersionNo;
  delete mainPileFunction.isDefaultVersion;

  mergeEmbeddedDeviceFormOntoEntity(mainPileFunction, form);
  if (form.softVersion != null && form.softVersion !== '') {
    mainPileFunction.softVersion = String(form.softVersion);
  }
  if (Number(form.defaultFlag) === 1) {
    mainPileFunction.defaultEmbeddedVersionNo =
      form.softVersion || mainPileFunction.defaultEmbeddedVersionNo;
  }
  normalizePileFunctionNumericFields(mainPileFunction);

  return {
    mainPileFunction,
    piletypeEntity,
    versionList,
  };
}

/**
 * onlyAddPileFunction：POST 请求体为单个实体（根级 JSON）
 * - 新增：saveMode 'add'
 * - 详情/编辑：saveMode 'edit' 且 rowIndex≥0，保留 pileFunctionId、pileTypeVersionId 等
 */
export function buildOnlyAddPileFunctionBody(
  parent,
  form,
  existingList,
  routePileTypeNo,
  saveMode = 'add',
  rowIndex = -1
) {
  const isEdit = saveMode === 'edit' && Number(rowIndex) >= 0;
  const { subPileFunction, versionList } = buildSavePileVersionPayload(
    parent,
    form,
    isEdit ? 'edit' : 'add',
    isEdit ? Number(rowIndex) : -1,
    existingList,
    routePileTypeNo
  );
  return {
    body: subPileFunction[0],
    versionList,
  };
}

/**
 * 主表新增/编辑：仅 mainPileFunction + piletypeEntity
 * softVersion、countdownReservation、currentSettingMemory（及 defaultEmbeddedVersionNo 同步）始终保留在 mainPileFunction
 */
export function buildMainSubPayloadForDetailSubmit(detailForm) {
  const mainPileFunction = stripEmbeddedCollections(detailForm);
  delete mainPileFunction.piletypeEntity;
  const piletypeEntity = buildPiletypeEntity(detailForm);
  removePiletypeEntityKeysFromRoot(mainPileFunction);
  // 图片同时属于 t_pile_function（C端按版本取图），从 piletypeEntity 回填，避免被 removePiletypeEntityKeysFromRoot 删掉
  mainPileFunction.pileImgUrl = piletypeEntity.pileImgUrl;
  normalizePileFunctionNumericFields(mainPileFunction);
  const mainSv = mainPileFunction.softVersion;
  if (mainSv !== undefined && mainSv !== null && String(mainSv).trim() !== '') {
    mainPileFunction.defaultEmbeddedVersionNo = String(mainSv);
  }

  return {
    mainPileFunction,
    piletypeEntity,
  };
}
